// Enemy definitions. Enemies share the stat shape with champions minus the trait
// list and ability — for M1 enemies just basic-attack. M2+ will add enemy abilities.

export type EnemyId =
  | "minion"
  | "casterMinion"
  | "redBuff"
  | "blueBuff"
  | "raptor"
  | "gromp"
  | "krug"
  | "wolf"
  | "scuttle"
  | "dragon"
  | "herald"
  | "baron"
  | "worlds";

export interface EnemyDef {
  id: EnemyId;
  displayName: string;
  baseHp: number;
  baseAd: number;
  baseAp: number;
  range: number;
  moveSpeed: number;
  baseAttackSpeed: number; // attacks per second
  iconPath: string; // local path in /images/
  xpReward: number;
}

// Works in both Vite (replaces import.meta.env) and Node.js (env is undefined)
const _BASE: string = import.meta.env?.BASE_URL ?? '/';

export const ENEMIES: Record<EnemyId, EnemyDef> = {
  minion: {
    id: "minion",
    displayName: "Minion",
    baseHp: 168,
    baseAd: 60,
    baseAp: 0,
    range: 1,
    moveSpeed: 2,
    baseAttackSpeed: 1,
    iconPath: `${_BASE}images/minion.png`,
    xpReward: 20,
  },
  casterMinion: {
    id: "casterMinion",
    displayName: "Caster Minion",
    baseHp: 120,
    baseAd: 27,
    baseAp: 75,
    range: 3,
    moveSpeed: 1,
    baseAttackSpeed: 1,
    iconPath: `${_BASE}images/minion.png`,
    xpReward: 20,
  },
  redBuff: {
    id: "redBuff",
    displayName: "Red Buff",
    baseHp: 408,
    baseAd: 136,
    baseAp: 0,
    range: 1,
    moveSpeed: 2,
    baseAttackSpeed: 0.6,
    iconPath: `${_BASE}images/redBuff.png`,
    xpReward: 40,
  },
  blueBuff: {
    id: "blueBuff",
    displayName: "Blue Buff",
    baseHp: 360,
    baseAd: 0,
    baseAp: 123,
    range: 3,
    moveSpeed: 1,
    baseAttackSpeed: 0.65,
    iconPath: `${_BASE}images/blueBuff.png`,
    xpReward: 40,
  },
  raptor: {
    id: "raptor",
    displayName: "Raptor",
    baseHp: 67,
    baseAd: 35,
    baseAp: 0,
    range: 1,
    moveSpeed: 2,
    baseAttackSpeed: 0.75,
    iconPath: `${_BASE}images/raptor.png`,
    xpReward: 30,
  },
  gromp: {
    id: "gromp",
    displayName: "Gromp",
    baseHp: 336,
    baseAd: 173,
    baseAp: 0,
    range: 1,
    moveSpeed: 1,
    baseAttackSpeed: 1.0,
    iconPath: `${_BASE}images/gromp.png`,
    xpReward: 35,
  },
  krug: {
    id: "krug",
    displayName: "Krug",
    baseHp: 42,
    baseAd: 21,
    baseAp: 0,
    range: 1,
    moveSpeed: 1,
    baseAttackSpeed: 0.6,
    iconPath: `${_BASE}images/krug.png`,
    xpReward: 30,
  },
  wolf: {
    id: "wolf",
    displayName: "Wolf",
    baseHp: 112,
    baseAd: 56,
    baseAp: 0,
    range: 1,
    moveSpeed: 2,
    baseAttackSpeed: 0.9,
    iconPath: `${_BASE}images/wolf.png`,
    xpReward: 25,
  },
  scuttle: {
    id: "scuttle",
    displayName: "Scuttle Crab",
    baseHp: 528,
    baseAd: 0,
    baseAp: 0,
    range: 1,
    moveSpeed: 1,
    baseAttackSpeed: 0,
    iconPath: `${_BASE}images/scuttle.png`,
    xpReward: 80,
  },
  dragon: {
    id: "dragon",
    displayName: "Dragon",
    baseHp: 900,
    baseAd: 108,
    baseAp: 0,
    range: 2,
    moveSpeed: 2,
    baseAttackSpeed: 0.5,
    iconPath: `${_BASE}images/dragon.png`,
    xpReward: 120,
  },
  herald: {
    id: "herald",
    displayName: "Rift Herald",
    baseHp: 1100,
    baseAd: 127,
    baseAp: 0,
    range: 1,
    moveSpeed: 2,
    baseAttackSpeed: 0.5,
    iconPath: `${_BASE}images/scuttle.png`, // placeholder
    xpReward: 160,
  },
  baron: {
    id: "baron",
    displayName: "Baron Nashor",
    baseHp: 2000,
    baseAd: 172,
    baseAp: 0,
    range: 2,
    moveSpeed: 2,
    baseAttackSpeed: 0.5,
    iconPath: `${_BASE}images/baron.png`,
    xpReward: 220,
  },
  worlds: {
    id: "worlds",
    displayName: "The Nexus",
    baseHp: 3400,
    baseAd: 217,
    baseAp: 120,
    range: 3,
    moveSpeed: 2,
    baseAttackSpeed: 0.55,
    iconPath: `${_BASE}images/worlds.png`,
    xpReward: 500,
  },
};
